import type { CrmDonor, CrmState, Donor, ErpGraph, LinkTarget } from '../../contract'
import { IDENTITY_CONFLICT_MESSAGE } from '../../contract'
import { can, type AdminRole } from '../../../admin/shared/permissions'

/* ERP · doador no CRM (lógica pura). Decisão 2026-10-24: a base guarda chaves e ligações; nome e contato (mascarado)
   vêm do CRM pelo Hub, só dos doadores que a tela mostra. Aqui: quais ids pedir (a página visível, em lotes), como juntar
   as respostas, o nome a mostrar, o cartão "No CRM" da ficha com estado honesto e as mensagens da ligação. */

/** Ids por pedido ao servidor (= uma página da lista); o servidor ainda manda ao Hub em lotes de 50. */
export const CRM_PAGE_IDS = 20
/** Mesmo filtro do Hub: 3 a 100 caracteres, só letras, espaço e ' . - (CPF, e-mail e curinga não vão ao CRM). */
const NAME_QUERY = /^[\p{L}][\p{L} '.-]*$/u

export function crmNameQuery(raw: string): string | null {
    const q = raw.trim().replace(/\s+/g, ' ')
    return q.length >= 3 && q.length <= 100 && NAME_QUERY.test(q) ? q : null
}

/** Digitado com cara de CPF (só dígitos e . - / espaço, 11 dígitos): os dígitos, válidos ou não; senão null. */
export function typedCpf(raw: string): string | null {
    const t = raw.trim()
    if (!/^[\d.\-/\s]+$/.test(t)) return null
    const d = t.replace(/\D/g, '')
    return d.length === 11 ? d : null
}

/** Dígitos verificadores do CPF (11 dígitos, não todos iguais). Mesma conta do servidor e do Hub. */
export function validCpf(d: string): boolean {
    if (!/^\d{11}$/.test(d) || /^(\d)\1{10}$/.test(d)) return false
    const check = (n: number) => {
        let sum = 0
        for (let i = 0; i < n; i++) sum += Number(d[i]) * (n + 1 - i)
        const r = sum % 11
        return r < 2 ? 0 : 11 - r
    }
    return check(9) === Number(d[9]) && check(10) === Number(d[10])
}

export type CrmQuery = { kind: 'name' | 'cpf'; q: string }

/** O que a busca de doadores manda ao servidor (no corpo, nunca na URL): CPF válido (só os dígitos) ou nome (3+ letras). */
export function crmDonorQuery(raw: string): CrmQuery | null {
    const cpf = typedCpf(raw)
    if (cpf) return validCpf(cpf) ? { kind: 'cpf', q: cpf } : null
    const name = crmNameQuery(raw)
    return name ? { kind: 'name', q: name } : null
}

/** Ids da base que valem a pena pedir ao CRM (só os ligados ao CRM; no exemplo, nenhum), em lotes. */
export function crmIdBatches(donors: readonly Pick<Donor, 'id' | 'crmId'>[], size = CRM_PAGE_IDS): string[][] {
    const ids = [...new Set(donors.filter(d => d.crmId).map(d => d.id))]
    const out: string[][] = []
    for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size))
    return out
}

export type CrmView = { state: CrmState | 'loading'; items: Record<string, CrmDonor> }
const WORST: (CrmState | 'loading')[] = ['forbidden', 'unavailable', 'loading', 'ok', 'off']

/** Junta as respostas dos lotes: itens de todos; o estado é o pior (sem acesso > fora > carregando > ok > sem CRM).
    Serve também às contas (empresas) do CRM: mesmo formato { state, items }. */
export function mergeCrm<T = CrmDonor>(results: readonly ({ state: CrmState; items: Record<string, T> } | undefined)[]): { state: CrmState | 'loading'; items: Record<string, T> } {
    if (!results.length) return { state: 'off', items: {} }
    const states = results.map(r => r?.state ?? 'loading')
    const state = WORST.find(s => states.includes(s)) ?? 'off'
    return { state, items: Object.assign({}, ...results.map(r => r?.items ?? {})) as Record<string, T> }
}

/** Nome a mostrar: o do CRM quando veio; senão o da base (que pode ser o rótulo "nome no CRM"). */
export const donorName = (d: Pick<Donor, 'name'>, crm: CrmDonor | null | undefined) => crm?.name || d.name

/** Doador com o que o CRM trouxe por cima (nome, cidade/UF e contato mascarado), para a ficha e as listas. */
export function withCrm(d: Donor, crm: CrmDonor | null | undefined): Donor {
    if (!crm) return d
    return {
        ...d,
        name: donorName(d, crm),
        city: crm.city ?? d.city,
        uf: crm.uf ?? d.uf,
        cpf: crm.cpf ?? d.cpf,
        email: crm.email ?? d.email,
        phone: crm.mobile ?? crm.phone ?? d.phone,
    }
}

export type CrmCard =
    | { kind: 'hidden' }
    | { kind: 'message'; aside: string; text: string; tone: 'neutral' | 'warn' }
    | { kind: 'details'; aside: string; rows: [string, string][]; pii: [string, string][] }

const dash = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? '—' : String(v))
const brDay = (iso: string | null) => {
    if (!iso) return '—'
    const t = Date.parse(iso)
    return Number.isNaN(t) ? '—' : new Date(t).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
}
const STATUS: Record<'active' | 'inactive', string> = { active: 'ativo no CRM', inactive: 'inativo no CRM' }

/** Cartão "No CRM" da ficha do doador: o que o CRM diz (mascarado) ou, se não deu, por quê — sem inventar. */
export function crmCard(d: Pick<Donor, 'id' | 'crmId'>, dataMode: ErpGraph['dataMode'], view: CrmView): CrmCard {
    if (dataMode === 'example') return { kind: 'hidden' }
    if (!d.crmId) return { kind: 'message', aside: 'sem ligação', tone: 'neutral', text: 'Este doador ainda não está ligado a um contato do CRM. Quando a ligação acontecer (ficha do Live, sincronização ou o Panel), os dados aparecem aqui.' }
    if (view.state === 'loading') return { kind: 'message', aside: 'lendo…', tone: 'neutral', text: 'Lendo o CRM…' }
    if (view.state === 'off') return { kind: 'message', aside: 'sem CRM', tone: 'neutral', text: 'Este ambiente não lê o CRM.' }
    if (view.state === 'forbidden') return { kind: 'message', aside: 'sem acesso', tone: 'warn', text: 'O Panel ainda não tem acesso ao CRM pelo Hub. A Tecnologia libera a chave do Panel; até lá, nome e contato não aparecem.' }
    if (view.state === 'unavailable') return { kind: 'message', aside: 'fora do ar', tone: 'warn', text: 'O CRM não respondeu agora (Hub ou Dynamics fora). As ligações da base continuam valendo; tente de novo em alguns minutos.' }
    const c = view.items[d.id]
    if (!c) return { kind: 'message', aside: 'não encontrado', tone: 'warn', text: 'O contato ligado não foi encontrado no CRM como doador (pode ter sido apagado ou mesclado). A Tecnologia confere em Configurações.' }
    return {
        kind: 'details',
        aside: c.modifiedAt ? `atualizado em ${brDay(c.modifiedAt)}` : 'Dynamics',
        rows: [
            ['Nome', dash(c.name)],
            ['Situação', [c.status ? STATUS[c.status] : null, c.statusReason].filter(Boolean).join(' · ') || '—'],
            ['Cidade', [c.city, c.uf].filter(Boolean).join('/') || '—'],
            ['Linhas ativas', dash(c.activeContractLines)],
            ['Faturas abertas', dash(c.openInvoices)],
            ['No CRM desde', brDay(c.createdAt)],
        ],
        pii: [['CPF', dash(c.cpf)], ['E-mail', dash(c.email)], ['Celular', dash(c.mobile)], ['Telefone', dash(c.phone)]],
    }
}

/** Aviso da busca por nome ou CPF no CRM, ou null quando não há o que dizer. */
export function crmSearchNote(state: CrmState | 'loading' | null, found: number, kind: CrmQuery['kind'] = 'name'): string | null {
    if (state === null || state === 'off') return null
    if (state === 'loading') return 'Procurando no CRM…'
    if (state === 'forbidden') return 'A busca no CRM ainda não está liberada para o Panel.'
    if (state === 'unavailable') return 'O CRM não respondeu agora; mostrando só o que está na base.'
    return found ? null : kind === 'cpf' ? 'Ninguém com esse CPF no CRM.' : 'Ninguém com esse nome no CRM.'
}

/** Estado de uma consulta ao CRM no cliente: o que o servidor disse; erro de rede = fora; sem resposta ainda = lendo. */
export function crmQueryState(data: { state: CrmState } | undefined, isError: boolean): CrmState | 'loading' {
    return data?.state ?? (isError ? 'unavailable' : 'loading')
}

/** O que a tela mostra do CRM: sem busca, os que chegaram por último; nome com 3+ letras ou CPF válido, a busca no CRM;
    CPF com os 11 dígitos errados, o aviso; o resto (1–2 letras, telefone, código) só filtra a base. */
export function crmSection(text: string): 'recent' | 'search' | 'bad-cpf' | null {
    if (!text.trim()) return 'recent'
    if (crmDonorQuery(text)) return 'search'
    return typedCpf(text) ? 'bad-cpf' : null
}

/** Aviso dos "chegaram por último" (lendo vira esqueleto, não texto). */
export function crmRecentNote(state: CrmState | 'loading' | null, shown: number): string | null {
    if (state === null || state === 'off' || state === 'loading') return null
    if (state === 'forbidden') return 'O CRM ainda não está liberado para o Panel. A Tecnologia libera a chave; até lá, só aparece o que está na base.'
    if (state === 'unavailable') return 'O CRM não respondeu agora. A lista da base continua valendo; tente de novo em alguns minutos.'
    return shown ? null : 'Nenhum doador novo no CRM agora.'
}

/** Os do CRM sem repetir: um por crmId e sem quem já aparece na lista da base acima. */
export function recentCrmRows<T extends { crmId: string; sponsorId: string | null }>(items: readonly T[], shownSponsorIds: Iterable<string>): T[] {
    const shown = new Set(shownSponsorIds)
    const seen = new Set<string>()
    return items.filter(c => {
        const key = c.crmId.toLowerCase()
        if (seen.has(key) || (c.sponsorId && shown.has(c.sponsorId))) return false
        seen.add(key)
        return true
    })
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** Linha de um doador do CRM: cidade/UF, linhas ativas, faturas em aberto e se já está na base. */
export function crmRowMeta(c: Pick<CrmDonor, 'city' | 'uf' | 'statusReason' | 'activeContractLines' | 'openInvoices'> & { sponsorId: string | null }, withStatus = false): string {
    return [
        [c.city, c.uf].filter(Boolean).join('/'),
        withStatus ? c.statusReason : null,
        c.activeContractLines != null ? count(c.activeContractLines, 'linha ativa', 'linhas ativas') : null,
        c.openInvoices ? count(c.openInvoices, 'fatura em aberto', 'faturas em aberto') : null,
        c.sponsorId ? 'na base' : 'só no CRM',
    ].filter(Boolean).join(' · ')
}

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

/** "desde mar/2024" pela data de entrada no CRM (createdon); null sem data. */
export function crmSince(iso: string | null): string | null {
    const m = /^(\d{4})-(\d{2})/.exec(iso ?? '')
    const month = m ? MONTHS[Number(m[2]) - 1] : undefined
    return m && month ? `desde ${month}/${m[1]}` : null
}

/** Linha compacta de um doador do CRM: a pílula da situação, cidade/UF e desde quando (só o que veio). */
export function crmRowCompact(c: Pick<CrmDonor, 'city' | 'uf' | 'status' | 'statusReason' | 'createdAt'>): { meta: string; pill: { label: string; tone: 'ok' | 'neutral' } | null } {
    const pill = c.status === 'active' ? { label: 'ativo', tone: 'ok' as const } : c.status === 'inactive' ? { label: 'inativo', tone: 'neutral' as const } : null
    return { meta: [[c.city, c.uf].filter(Boolean).join('/'), crmSince(c.createdAt)].filter(Boolean).join(' · '), pill }
}

/** Detalhe que fica no title da linha (passar o mouse): situação no CRM, linhas, faturas e contato mascarado. */
export function crmRowTitle(c: Pick<CrmDonor, 'statusReason' | 'activeContractLines' | 'openInvoices' | 'cpf' | 'email' | 'mobile' | 'phone'> & { sponsorId: string | null }): string {
    return [
        c.statusReason,
        c.activeContractLines != null ? count(c.activeContractLines, 'linha ativa', 'linhas ativas') : null,
        c.openInvoices ? count(c.openInvoices, 'fatura em aberto', 'faturas em aberto') : null,
        c.sponsorId ? 'na base' : 'só no CRM',
        crmContactTitle(c),
    ].filter(Boolean).join(' · ')
}

/** Contato mascarado (já vem assim do Hub) para o title da linha; null se o CRM não tem nenhum. */
export function crmContactTitle(c: Pick<CrmDonor, 'cpf' | 'email' | 'mobile' | 'phone'>): string | null {
    const parts = [c.cpf && `CPF ${c.cpf}`, c.email && `e-mail ${c.email}`, (c.mobile || c.phone) && `tel. ${c.mobile || c.phone}`].filter(Boolean)
    return parts.length ? parts.join(' · ') : null
}

/** Quem pode ligar um doador a igreja, empresa, embaixador ou evento (o servidor confere de novo). */
export const canLinkDonor = (role: AdminRole) => can(role, 'fichas.editar') || can(role, 'parceiros') || can(role, 'embaixadores')

export const LINK_TARGET_LABEL: Record<LinkTarget, string> = { igreja: 'Igreja (frequenta)', embaixador: 'Embaixador (trouxe)', empresa: 'Empresa (trabalha em)', evento: 'Evento (de origem)' }

/** Mensagem do erro da ligação: o conflito de identidade com o texto combinado; o resto, curto. */
export function linkErrorMessage(error: { status?: number; code?: string; detail?: string | null } | null | undefined): string {
    if (!error) return 'Não deu para ligar. Tente de novo.'
    if (error.code === 'conflict') return error.detail || IDENTITY_CONFLICT_MESSAGE
    if (error.code === 'forbidden') return 'O seu perfil não pode ligar doadores.'
    if (error.code === 'not_found') return 'Não achei o doador ou o destino na base.'
    if (error.code === 'unavailable' || error.code === 'network') return 'A base não respondeu agora. Tente de novo em alguns minutos.'
    return 'Não deu para ligar. Tente de novo.'
}
